import { useContext, useMemo } from "react";

import { Logger } from "~/tools/logger";
import type { Record } from "~/tools/record";

import type { CaptureEvents } from "./_capture-events";
import { CaptureBoundaryContext } from "./_context";
import { usePostHog } from "./_use-post-hog";
import type { CaptureFn } from "./capture-fn";

const logger = Logger.namespace("analytics:capture");

/**
 * The interface defines all the events that can be captured.
 * An event can either have a payload (Record<string, unknown>) or not (never).
 */
export type Capture = {
	[TEvent in keyof CaptureEvents]: CaptureEvents[TEvent] extends never
		? CaptureFn<TEvent>
		: CaptureFn<TEvent, [payload: CaptureEvents[TEvent]]>;
};

export function useCapture(): Capture {
	const posthog = usePostHog();
	const boundary = useContext(CaptureBoundaryContext).join(" > ");

	return useMemo(() => {
		// proxy makes it easier to create a function for each event
		// so it is easier to find out which events are used via devtools
		return new Proxy(
			{},
			{
				get(_, eventName: keyof CaptureEvents) {
					return (payload?: Record<string>) => {
						try {
							const payloadWithContext =
								boundary.length > 0
									? {
											...payload,
											__context__: { boundary },
										}
									: payload;

							if (import.meta.env.DEV) {
								logger.info(eventName, JSON.stringify(payloadWithContext, null, 2));
							} else if (eventName !== "noop") {
								posthog?.capture(eventName, payloadWithContext);
							}
						} catch {
							// do not fail if anything goes wrong
							logger.warn("Failed to capture event", eventName, payload);
						}
					};
				},
			},
		) as Capture;
	}, [posthog, boundary]);
}
